import {
  AlignType,
  EHeadingStyleVariant,
  EModalVariants,
  ETextStyleVariant,
  GeneralCalendarIcon,
  GeneralCaretRightIcon,
  Heading,
  Modal,
  Selector,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import SearchPreviewResultsContainer from '@/components/common/SearchPreviewResults/SearchPreviewResultsContainer';
import { TAmenitySlug } from '@/constants/amenitiesFilter';
import { ESearchSheetContent } from '@/constants/search';
import { ESearchFilters } from '@/constants/searchFilters';
import {
  useInitialAddress,
  useInitialDates,
  useInitialGuests,
  useInitialInputs,
  useInitialSearchMode,
} from '@/hooks/useInitialInputs';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { ISearchFormFilters, setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { formatDateRangeWithFlexibleDays } from '@/utility/format-date';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

import { INavbarSearch } from '../../SearchHeader';
import { MobileSearchAddressModalContent } from './MobileSearchAddressModalContent';
import { MobileSearchDatesModalContent } from './MobileSearchDatesModalContent';
import { MobileSearchGuestsModalContent } from './MobileSearchGuestsModalContent';

type TMobileSearchModalProps = {
  location?: { lat: number; lng: number };
  address: INavbarSearch['address'];
  dates: INavbarSearch['dates'];
  guests: INavbarSearch['guests'];
  isSearchModalOpen: boolean;
  onCloseHandler: () => void;
  onSubmit?: () => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onChangeDates?: (dates?: IHeaderSearchFilterDates) => void;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  recentSearches?: ILocationSearchAutocompleteListOption[];
  handleRecentSearchNavigation?: (url: string) => void;
  isGuestOccupancyAvailable?: boolean;
  hasVariant: boolean;
  disabledDays?: DayPickerProps['disabled'];
};

type TFilterContent = {
  useActions: boolean;
  header?: React.ReactNode;
  content: React.ReactNode;
  footer?: React.ReactNode;
};

export const MobileSearchModal = ({
  location,
  address,
  dates,
  guests,
  isSearchModalOpen,
  onCloseHandler,
  onSubmit,
  onChangeGuests,
  onChangeDates,
  onChangeAddress,
  popularDestinations,
  recentSearches,
  handleRecentSearchNavigation,
  isGuestOccupancyAvailable,
  hasVariant,
  disabledDays = [],
}: TMobileSearchModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentQueryParams = useSelector((state: TRootState) => state.queryParams);
  const searchFormFilters = useSelector((state: TRootState) => state.searchForm.filters);

  const [filterContent, setFilterContent] = useState<ESearchSheetContent>(ESearchSheetContent.HOME);

  const isDeliveryMode = isDeliveryModeFromParams(currentQueryParams);

  const { hasChanges: modeHasChanges } = useInitialSearchMode(isDeliveryMode, isSearchModalOpen);
  const { hasChanges: addressHasChanges } = useInitialAddress(address || '', isSearchModalOpen);
  const { initialValue: initialDates, hasChanges: datesHasChanges } = useInitialDates(
    {
      from: dates?.from,
      to: dates?.to,
      flexible_days: dates?.flexible_days,
    },
    isSearchModalOpen,
  );
  const { initialValue: initialGuests, hasChanges: guestsHasChanges } = useInitialGuests(
    {
      adults: guests?.adults,
      children: guests?.children,
      infants: guests?.infants,
      accessible: guests?.accessible,
      pet_friendly: guests?.pet_friendly,
    },
    isSearchModalOpen,
  );

  const hasChanges = modeHasChanges || addressHasChanges || datesHasChanges || guestsHasChanges;

  const { initialValue: initialSearchFormFilters } = useInitialInputs(
    searchFormFilters,
    isSearchModalOpen,
  );

  const handleAddressChange = async (newAddress?: string, addressOption?: any) => {
    onChangeAddress?.(newAddress, addressOption);

    if (newAddress) {
      let fullMapboxAddress = '';
      let mapboxId = '';
      if (addressOption) {
        const { value } = addressOption;
        mapboxId = value?.mapbox_id || value?.id;

        if (mapboxId) {
          fullMapboxAddress = await retrieveMapboxFullAddress(value);
        }
      }

      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.ADDRESS]: newAddress,
            [ESearchFilters.DELIVERY_ADDRESS]: newAddress,
            [ESearchFilters.MAPBOX_ID]: mapboxId || undefined,
            [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddress || undefined,
            ...(isDeliveryMode
              ? {
                  [ESearchFilters.DELIVERY]: true,
                  [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
                }
              : {
                  [ESearchFilters.DELIVERY]: undefined,
                  [ESearchFilters.DELIVERY_STATIONARY]: undefined,
                }),
          },
          filterMethod: null,
        }),
      );
    }
  };

  const handleDatesChange = (newDates?: IHeaderSearchFilterDates) => {
    onChangeDates?.(newDates);

    const formDateFilters = {
      [ESearchFilters.DATE_FROM]: newDates?.from ? dayjs(newDates?.from).format('YYYY-MM-DD') : '',
      [ESearchFilters.DATE_TO]: newDates?.to ? dayjs(newDates?.to).format('YYYY-MM-DD') : '',
      [ESearchFilters.FLEXIBLE_DAYS]: newDates?.flexible_days,
    };

    dispatch(
      setFormFilter({
        filters: formDateFilters,
        filterMethod: null,
      }),
    );
  };

  const handleGuestsChange = (newGuests?: IHeaderSearchFilterGuests) => {
    onChangeGuests?.(newGuests);

    if (newGuests) {
      const filtersToUpdate: Partial<ISearchFormFilters> = {};
      const queryFiltersToUpdate: Record<string, string | undefined> = {};

      const features: string[] = [];
      if (newGuests.accessible) features.push('accessible');
      if (newGuests.pet_friendly) features.push('pet_friendly');

      filtersToUpdate[ESearchFilters.FILTER_FEATURE] =
        features.length > 0 ? (features as TAmenitySlug[]) : undefined;
      queryFiltersToUpdate[ESearchFilters.FILTER_FEATURE] =
        features.length > 0 ? features.join(',') : undefined;

      const guestFilters = {
        [ESearchFilters.GUESTS_ADULTS]: newGuests.adults,
        [ESearchFilters.GUESTS_CHILDREN]: newGuests.children,
        [ESearchFilters.GUESTS_INFANTS]: newGuests.infants,
      };

      dispatch(
        setFormFilter({
          filters: {
            ...filtersToUpdate,
            ...guestFilters,
          },
          filterMethod: null,
        }),
      );
    }
  };

  const onClose = () => {
    const resetAddress = initialSearchFormFilters[ESearchFilters.ADDRESS];
    const resetMapboxId = initialSearchFormFilters[ESearchFilters.MAPBOX_ID];
    const resetFullMapboxAddress = initialSearchFormFilters[ESearchFilters.FULL_MAPBOX_ADDRESS];
    const option = {
      value: {
        mapbox_id: resetMapboxId,
        full_address: resetFullMapboxAddress,
      },
    };

    handleAddressChange(resetAddress, option);

    handleDatesChange({
      from: initialDates?.from,
      to: initialDates?.to,
      flexible_days: !initialDates?.flexible_days ? undefined : initialDates?.flexible_days,
    });

    handleGuestsChange({
      adults: initialGuests?.adults,
      children: initialGuests?.children,
      infants: initialGuests?.infants,
      accessible: !initialGuests?.accessible ? false : initialGuests?.accessible,
      pet_friendly: !initialGuests?.pet_friendly ? false : initialGuests?.pet_friendly,
    });

    onCloseHandler();
  };

  const handleSubmit = () => {
    onSubmit?.();
    onCloseHandler();
  };

  const formattedDates = formatDateRangeWithFlexibleDays(
    dates?.from,
    dates?.to,
    dates?.flexible_days,
  );

  const totalGuests = (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0) || 0;
  const guestsLabel = intl.formatMessage(
    {
      defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
      id: 'ErPlEg',
      description: 'Global Header > label for guests button on mobile bar',
    },
    {
      totalGuests,
    },
  );

  const datesLabel = formattedDates
    ? formattedDates
    : intl.formatMessage({
        defaultMessage: 'Add dates',
        id: 'zP42BU',
        description: 'Global Header > Placeholder text for dates button on mobile bar',
      });

  const getLocationLabel = () => {
    return intl.formatMessage({ defaultMessage: 'Location', id: 'rvirM2' });
  };

  const getLocationDescription = () => {
    const formattedAddress = address?.split('•').map(part => part.trim())[0];
    if (formattedAddress) return formattedAddress;

    return getLocationLabel();
  };

  const onSelectFilterContentHandler = (selectedFilterContent: ESearchSheetContent) => {
    setFilterContent(selectedFilterContent);
  };

  const tripSheetHomeContent = (
    <Modal.Content className="py-2">
      <div className="flex flex-col gap-6">
        {!hasVariant && (
          <Selector
            name="location"
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Please select a location.',
              id: '1h5k89',
            })}
            label={address ? getLocationLabel() : ''}
            description={getLocationDescription()}
            value={address || ''}
            onChange={() => onSelectFilterContentHandler(ESearchSheetContent.ADDRESS)}
            contentWrapperClassName={cn('!py-3 !px-4', {
              '!py-5': !address,
            })}
            descriptionClassName="truncate"
            labelVariant={ETextStyleVariant.SmallBold}
            descriptionVariant={ETextStyleVariant.MediumRegular}
          />
        )}
        <Selector
          name="dates"
          ariaLabel={intl.formatMessage({
            defaultMessage: 'Please select some dates.',
            id: '/1Vehk',
          })}
          icon={GeneralCalendarIcon}
          label={intl.formatMessage({
            defaultMessage: 'Dates',
            id: 'D6b0Au',
          })}
          description={datesLabel}
          value={formattedDates || ''}
          onChange={() => onSelectFilterContentHandler(ESearchSheetContent.DATES)}
          contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
          labelVariant={ETextStyleVariant.SmallBold}
          descriptionVariant={ETextStyleVariant.MediumRegular}
          alignVariant={AlignType.HorizontalRight}
        />
        <Selector
          name="guests"
          ariaLabel={intl.formatMessage({
            defaultMessage: 'Please select some guests.',
            id: 'wGpMIJ',
          })}
          icon={GeneralCaretRightIcon}
          label={intl.formatMessage({ defaultMessage: 'Guest', id: 'zF7Cth' })}
          description={
            totalGuests
              ? guestsLabel
              : intl.formatMessage({ defaultMessage: 'Add guests', id: 'pP5ifN' })
          }
          value={guestsLabel || ''}
          onChange={() => onSelectFilterContentHandler(ESearchSheetContent.GUESTS)}
          contentWrapperClassName="!py-3 !px-4 [&>div]:w-full [&>div]:justify-between"
          labelVariant={ETextStyleVariant.SmallBold}
          descriptionVariant={ETextStyleVariant.MediumRegular}
          alignVariant={AlignType.HorizontalRight}
        />
      </div>
    </Modal.Content>
  );

  const tripSheetContent: Record<ESearchSheetContent, TFilterContent> = {
    [ESearchSheetContent.HOME]: {
      useActions: true,
      header: (
        <Modal.Header inlineHeader={true} className="mb-6 !pt-4 !pb-0">
          <Heading variant={EHeadingStyleVariant.H6}>
            {intl.formatMessage({ defaultMessage: 'Trip information', id: 'MrJVmR' })}
          </Heading>
        </Modal.Header>
      ),
      content: tripSheetHomeContent,
      footer: (
        <Modal.Footer className="!justify-end">
          <SearchPreviewResultsContainer onSubmit={handleSubmit} isDisabled={!hasChanges} />
        </Modal.Footer>
      ),
    },
    [ESearchSheetContent.ADDRESS]: {
      useActions: false,
      content: (
        <MobileSearchAddressModalContent
          onCloseHandler={() => setFilterContent(ESearchSheetContent.HOME)}
          location={location}
          address={address}
          onChangeAddress={handleAddressChange}
          popularDestinations={popularDestinations}
          recentSearches={recentSearches}
          handleRecentSearchNavigation={handleRecentSearchNavigation}
        />
      ),
    },
    [ESearchSheetContent.DATES]: {
      useActions: false,
      content: (
        <MobileSearchDatesModalContent
          onCloseHandler={() => setFilterContent(ESearchSheetContent.HOME)}
          dates={dates}
          onChangeDates={handleDatesChange}
          disabledDays={disabledDays}
        />
      ),
    },
    [ESearchSheetContent.GUESTS]: {
      useActions: false,
      content: (
        <MobileSearchGuestsModalContent
          onCloseHandler={() => setFilterContent(ESearchSheetContent.HOME)}
          guests={guests}
          onChangeGuests={handleGuestsChange}
          isGuestOccupancyAvailable={isGuestOccupancyAvailable}
        />
      ),
    },
  };

  return (
    <Modal
      isOpen={isSearchModalOpen}
      onClose={onClose}
      variant={EModalVariants.Bottom}
      className="z-[1410]">
      {tripSheetContent[filterContent].useActions && <Modal.Actions className="top-5" />}
      {tripSheetContent[filterContent]?.header}
      {tripSheetContent[filterContent]?.content}
      {tripSheetContent[filterContent]?.footer}
    </Modal>
  );
};
